import { motion, AnimatePresence } from 'framer-motion';
import { useGetDesignationsQuery } from 'redux/features/designationApi';
import Select from 'components/ui/Select';
import { useState, useEffect } from 'react';
import { FileText, Image as ImageIcon, X } from 'lucide-react';
import CommonDocumentPreview from './CommonDocumentPreview';

const EVERYONE_VALUE = 0;

// Helper to preview file before upload
// Helper to preview file before upload
const FilePreviewFromFile = ({ file }) => {
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }

    const isImage = file.type.startsWith('image/');
    if (isImage) {
      const objectUrl = URL.createObjectURL(file);
      setPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else {
      setPreview('pdf');
    }
  }, [file]);

  if (!file) return null;

  return (
    <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      {preview === 'pdf' || file.type === 'application/pdf' ? (
        <div className="flex items-center gap-3 p-3 bg-gray-50">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100/50 text-red-600">
            <FileText className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="truncate text-sm font-medium text-gray-900">{file.name}</p>
            <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
          </div>
        </div>
      ) : (
        <>
          <img src={preview} alt="Preview" className="h-40 w-full object-cover" />
          <div className="flex items-center gap-3 p-3 border-t border-gray-100">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100/50 text-blue-600">
              <ImageIcon className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-medium text-gray-900">{file.name}</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const AddRuleModal = ({ open, onClose, onSave, isLoading, initialData }) => {
  const [title, setTitle] = useState('');
  const [type, setType] = useState('TEXT');
  const [scope, setScope] = useState([EVERYONE_VALUE]);
  const [rules, setRules] = useState(['']);
  const [file, setFile] = useState(null);
  const [designationOptions, setDesignationOptions] = useState([]);

  const { data: designations } = useGetDesignationsQuery({});

  useEffect(() => {
    if (designations) {
      const options = [
        { label: 'Everyone', value: EVERYONE_VALUE },
        ...designations.map((d) => ({
          label: d.name,
          value: d.id,
        })),
      ];
      setDesignationOptions(options);

      // If scope has 'Everyone' (e.g. from initial '0' state), expand it to all values
      setScope((prev) => {
        if (prev.includes(EVERYONE_VALUE)) {
          return options.map((o) => o.value);
        }
        return prev;
      });
    }
  }, [designations]);

  useEffect(() => {
    if (initialData && open) {
      setTitle(initialData.title || '');
      setType(initialData.type || 'TEXT');
      // If initialData has scope, use it, otherwise default
      if (initialData.scope && Array.isArray(initialData.scope)) {
        const hasEveryone = initialData.scope.includes(EVERYONE_VALUE);
        if (hasEveryone && designationOptions.length > 0) {
          setScope(designationOptions.map((d) => d.value));
        } else {
          setScope(initialData.scope);
        }
      } else {
        if (designationOptions.length > 0) {
          setScope(designationOptions.map((d) => d.value));
        } else {
          setScope([EVERYONE_VALUE]);
        }
      }

      if (initialData.type === 'TEXT') {
        setRules(initialData.rules && initialData.rules.length > 0 ? initialData.rules : ['']);
      } else {
        setRules(['']);
      }
      setFile(null); // Files are usually not pre-populated in a file input
    } else if (open && !initialData) {
      // Add Mode: Ensure scope is expanded if it's default
      if (designationOptions.length > 0) {
        setScope(designationOptions.map((d) => d.value));
      } else {
        setScope([EVERYONE_VALUE]);
      }
    } else if (!open) {
      resetForm();
    }
  }, [initialData, open, designationOptions]);

  const resetForm = () => {
    setTitle('');
    setType('TEXT');
    setScope([EVERYONE_VALUE]);
    setRules(['']);
    setFile(null);
  };

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  const handleAddRule = () => {
    setRules((prev) => [...prev, '']);
  };

  const handleRemoveRule = (index) => {
    setRules((prev) => prev.filter((_, i) => i !== index));
  };

  const hasChanges = () => {
    if (!initialData) return true;

    if (title !== (initialData.title || '')) return true;
    if (type !== (initialData.type || 'TEXT')) return true;

    // Check scope
    const normalizedScope = [...scope].sort();
    const initialScope = initialData.scope || [];
    const normalizedInitialScope = (
      initialScope.includes(EVERYONE_VALUE) && designationOptions.length > 0
        ? designationOptions.map((o) => o.value)
        : initialScope
    ).sort();

    if (normalizedScope.length !== normalizedInitialScope.length) return true;
    if (!normalizedScope.every((val, index) => val === normalizedInitialScope[index])) return true;

    if (type === 'TEXT') {
      const initialRules = initialData.rules || [];
      if (rules.length !== initialRules.length) return true;
      if (!rules.every((r, i) => r === initialRules[i])) return true;
    }

    if (type === 'FILE' && file !== null) return true;

    return false;
  };

  const handleRuleChange = (index, value) => {
    setRules((prev) => prev.map((rule, i) => (i === index ? value : rule)));
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0] || null;
    setFile(selected);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isLoading) return;

    if (onSave) {
      onSave({
        title,
        type,
        scope: scope.includes(EVERYONE_VALUE) ? [EVERYONE_VALUE] : scope, // Send optimized scope
        rules: type === 'TEXT' ? rules.filter((r) => r.trim() !== '') : [],
        file: type === 'FILE' ? file : null,
      });
    }
  };

  const handleScopeChange = (newScope) => {
    const wasEveryoneSelected = scope.includes(EVERYONE_VALUE);
    const isEveryoneSelected = newScope.includes(EVERYONE_VALUE);
    const allValues = designationOptions.map((d) => d.value);
    const allValuesWithoutEveryone = allValues.filter((v) => v !== EVERYONE_VALUE);

    // Case 1: "Everyone" was just selected -> Select ALL
    if (!wasEveryoneSelected && isEveryoneSelected) {
      setScope(allValues);
      return;
    }

    // Case 2: "Everyone" was just deselected -> Deselect ALL
    if (wasEveryoneSelected && !isEveryoneSelected) {
      // Check if it was a direct deselect of "Everyone" (meaning newScope is everything except everyone)
      // Or if it was a deselect of something else which caused Everyone to be removed (logic below)
      // Actually, if !isEveryoneSelected, it means '0' is missing.
      // If we want "Deselect Everyone" to clear all, we setScope([]).

      // However, we need to distinguish between "User clicked Everyone to uncheck it" vs "User unchecked a specific item"
      // Wait, if user unchecks a specific item, "Everyone" should be removed, but the rest remain.
      // If user unchecks "Everyone" specifically, everything should be gone.

      // Let's rely on what changed.
      // If newScope length is Scope.length - 1, and the missing one is EVERYONE_VALUE -> User unchecked Everyone.
      if (
        scope.length === newScope.length + 1 &&
        !newScope.includes(EVERYONE_VALUE) &&
        scope.includes(EVERYONE_VALUE)
      ) {
        setScope([]);
        return;
      }
    }

    // Case 3: User changed specific items
    // If all specific items are selected, ensure "Everyone" is selected.
    // If not all specific items are selected, ensure "Everyone" is NOT selected.

    const specificSelections = newScope.filter((s) => s !== EVERYONE_VALUE);
    const areAllSpecificSelected =
      specificSelections.length > 0 &&
      specificSelections.length === allValuesWithoutEveryone.length;

    if (areAllSpecificSelected) {
      if (!newScope.includes(EVERYONE_VALUE)) {
        setScope([...specificSelections, EVERYONE_VALUE]);
      } else {
        setScope(newScope);
      }
    } else {
      if (newScope.includes(EVERYONE_VALUE)) {
        setScope(specificSelections);
      } else {
        setScope(newScope);
      }
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 "
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="relative w-full max-w-lg max-h-[80vh] flex flex-col rounded-2xl bg-white shadow-xl mx-4 overflow-hidden"
            initial={{ y: 20, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            {/* Header */}
            <div className="flex-shrink-0 flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {initialData ? 'Edit Rules' : 'Add New Rules'}
              </h2>
              <button
                type="button"
                onClick={handleClose}
                disabled={isLoading}
                className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-all focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <span className="sr-only">Close</span>
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5 custom-scrollbar">
                {/* Title */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Title
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    disabled={isLoading}
                    minLength={3}
                    maxLength={50}
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all"
                    placeholder="e.g. Attendance Policy"
                  />
                  <div className="flex justify-between px-1">
                    <p className="text-[10px] text-slate-400">Min 3, Max 50 characters</p>
                    <p
                      className={`text-[10px] ${title.trim().length > 50 || (title.trim().length > 0 && title.trim().length < 3) ? 'text-red-500 font-bold' : 'text-slate-400'}`}
                    >
                      {title.trim().length}/50
                    </p>
                  </div>
                </div>

                {/* Role selection -> Scope MultiSelect */}
                <div className="space-y-1.5">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Scope
                    </label>
                    <span className="text-[11px] text-slate-400">
                      Choose who’s allowed to see these rules.
                    </span>
                  </div>

                  <Select
                    multiple
                    options={designationOptions}
                    value={scope}
                    onChange={handleScopeChange}
                    placeholder="Select designations..."
                    className="w-full"
                    searchable
                    renderValue={(selected) => {
                      if (selected.some((s) => s.value === EVERYONE_VALUE)) return 'Everyone';
                      if (selected.length === 1) return selected[0].label;
                      return `${selected.length} Designation Selected`;
                    }}
                  />
                </div>

                {/* Type selection */}
                <div className="space-y-1.5 ">
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Type
                  </label>
                  <div className="inline-flex w-full rounded-lg border border-gray-200 bg-blue-100 p-1 gap-1">
                    <button
                      type="button"
                      disabled={isLoading}
                      onClick={() => setType('TEXT')}
                      className={`flex-1 rounded-md px-4 py-2 outline-none focus:outline-none text-sm font-medium transition-all ${
                        type === 'TEXT'
                          ? 'bg-white text-gray-900 shadow-sm ring-1 ring-black/5'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100/50'
                      }`}
                    >
                      Text
                    </button>
                    <button
                      type="button"
                      disabled={isLoading}
                      onClick={() => setType('FILE')}
                      className={`flex-1 rounded-md outline-none focus:outline-none px-4 py-2 text-sm font-medium transition-all ${
                        type === 'FILE'
                          ? 'bg-white text-gray-900 shadow-sm ring-1 ring-black/5'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100/50'
                      }`}
                    >
                      File
                    </button>
                  </div>
                </div>

                {/* Conditional: Text → multiple rules input */}
                {type === 'TEXT' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Rules List
                      </label>
                      <button
                        type="button"
                        disabled={isLoading}
                        onClick={handleAddRule}
                        className="text-xs font-semibold text-primary hover:text-primary-600 transition-colors"
                      >
                        + Add Rule
                      </button>
                    </div>

                    <div className="max-h-56 space-y-2.5 overflow-y-auto pr-2 custom-scrollbar">
                      {rules.map((rule, index) => (
                        <div key={index} className="flex items-center gap-2 group">
                          <input
                            type="text"
                            value={rule}
                            disabled={isLoading}
                            onChange={(e) => handleRuleChange(index, e.target.value)}
                            className="flex-1 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20 transition-all"
                            placeholder={`Rule description ${index + 1}`}
                          />
                          <button
                            type="button"
                            disabled={isLoading || rules.length === 1}
                            onClick={() => handleRemoveRule(index)}
                            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-transparent text-gray-400 hover:bg-red-50 hover:text-red-500 transition-all disabled:opacity-0"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Conditional: File → file input + preview */}
                {type === 'FILE' && (
                  <div className="space-y-2.5">
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Document Attachment
                    </label>
                    <label className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/50 px-4 py-8 text-center hover:border-primary-300 hover:bg-primary-50/30 transition-all group">
                      <input
                        type="file"
                        className="hidden"
                        disabled={isLoading}
                        onChange={handleFileChange}
                        accept="image/*,.pdf"
                      />
                      <div className="rounded-2xl bg-white p-3 shadow-sm border border-slate-100 group-hover:scale-110 transition-transform">
                        <svg
                          className="h-6 w-6 text-primary"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12 16V4M12 4L8 8M12 4L16 8M5 16V18C5 19.1046 5.89543 20 7 20H17C18.1046 20 19 19.1046 19 18V16"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <p className="mt-3 text-xs font-bold text-slate-700 uppercase tracking-tight">
                        Drop your file here
                      </p>
                      <p className="text-[10px] text-slate-500 font-medium">
                        PNG, JPG or PDF up to 5MB
                      </p>
                    </label>

                    {/* Preview */}
                    {file ? (
                      <FilePreviewFromFile file={file} />
                    ) : initialData?.document ? (
                      <div className="mt-3">
                        <p className="text-[10px] font-bold text-slate-700 mb-1">
                          Current document:
                        </p>
                        <CommonDocumentPreview document={initialData.document} />
                      </div>
                    ) : null}
                  </div>
                )}
              </div>

              {/* Footer buttons */}
              <div className="flex-shrink-0 flex justify-end gap-3 border-t border-gray-100 px-6 py-4 bg-gray-50/50">
                <button
                  type="button"
                  disabled={isLoading}
                  onClick={handleClose}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    isLoading ||
                    title.trim().length < 3 ||
                    title.trim().length > 50 ||
                    scope.length === 0 ||
                    (type === 'TEXT' && rules.filter((r) => r.trim() !== '').length === 0) ||
                    (type === 'FILE' && !file && (!initialData || !initialData.document)) ||
                    (initialData && !hasChanges())
                  }
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 focus:ring-2 focus:ring-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Saving...' : initialData ? 'Update Rule' : 'Create Rule'}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AddRuleModal;
